/**
 * fieldLayoutUtils.ts
 *
 * Utility functions for handling field layout and width calculations
 * in the form builder's flexible layout system.
 */

import { Field } from "../types/types";
import { cn } from "../lib/utils";

/**
 * Field width type definition
 */
export type FieldWidth = "full" | "half" | "third" | "quarter";

/**
 * Get the width setting for a field, defaulting to "full" if not specified
 */
export const getFieldWidth = (field: any): FieldWidth => {
  return field?.layout?.width || "full";
};

/**
 * Get Tailwind CSS classes for field container based on configured width
 *
 * Mobile (<1024px): All fields are full width for better UX
 * Desktop (>=1024px): Respect configured widths with auto-wrapping
 *
 * Uses calc() to account for gap spacing between fields
 */
export const getFieldContainerClasses = (field: any): string => {
  const width = getFieldWidth(field);

  return cn(
    "w-full transition-all", // Mobile: always full width, smooth transitions
    // Desktop widths with gap compensation
    width === "half" && "lg:w-[calc(50%-0.25rem)]",      // 2 per row
    width === "third" && "lg:w-[calc(33.333%-0.334rem)]", // 3 per row
    width === "quarter" && "lg:w-[calc(25%-0.375rem)]",   // 4 per row
  );
};

/**
 * Get numeric percentage for a width value (for display purposes)
 */
export const getFieldWidthPercentage = (width: FieldWidth): number => {
  const widthMap: Record<FieldWidth, number> = {
    full: 100,
    half: 50,
    third: 33,
    quarter: 25,
  };
  return widthMap[width];
};

/**
 * Get display label for width value
 */
export const getFieldWidthLabel = (width: FieldWidth): string => {
  const labelMap: Record<FieldWidth, string> = {
    full: "Full Width",
    half: "Half Width",
    third: "Third Width",
    quarter: "Quarter Width",
  };
  return labelMap[width];
};

/**
 * Calculate how many fields can fit in a row at a given width
 */
export const getFieldsPerRow = (width: FieldWidth): number => {
  const perRowMap: Record<FieldWidth, number> = {
    full: 1,
    half: 2,
    third: 3,
    quarter: 4,
  };
  return perRowMap[width];
};

/**
 * Determine if a field type should recommend full width
 * Some field types work better at full width
 */
export const shouldRecommendFullWidth = (fieldType: string): boolean => {
  const fullWidthTypes = [
    "textarea",
    "signature-pad",
    "multi_image_capture",
    "information-box",
    "comment-box",
  ];
  return fullWidthTypes.includes(fieldType);
};

/**
 * Get suggested width for a field type
 */
export const getSuggestedWidth = (fieldType: string): FieldWidth => {
  return shouldRecommendFullWidth(fieldType) ? "full" : "half";
};
